"use client";
import useDebounce from "@/app/hooks/useDebounce";
import useNotificationToken from "@/app/hooks/useNotificationToken";
import { Button } from "@/app/ui/forms/button";
import ButtonNF from "@/app/ui/forms/ButtonNF";
import AnimatedIcon from "@/app/ui/utils/AnimatedIcon";
import Section from "@/app/ui/utils/Section";
import { NotificationTopics } from "@/firebase/notifications/Notification.schemes";
import { notificationsCheckSubscription_action, notificationsRegisterToTopic_action, notificationsUnregisterFromTopic_action } from "@/lib/actions/notifications.actions";
import { ActionResponseType } from "@/lib/definitions";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import { isIOS } from "react-device-detect";
import { CgOptions } from "react-icons/cg";
import { FaBell, FaSadTear } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";
import { IoAddCircleOutline, IoShareOutline } from "react-icons/io5";
import { toast } from "sonner";
import useSWR, { SWRConfiguration_Custom, SWRResponse } from "swr";
import CustomDialog from "../CustomDialog";
import { NotificationInstructions } from "./NotificationNoPermissionPrompt/NotificationInstructions";



const callToActionList: Record<keyof typeof NotificationTopics, string> = {
  "videos": "כשמתפרסמים סרטונים חדשים של חברי נייטפול?",
  "creator": "כש{x} מפרסם סרטון חדש?",
  "generic": "כשדברים גדולים קורים בנייטפול?",
  "test": "כשמתבצעת בדיקת התראות?",
}


interface NotificationButtonProps {
  /** optional creator - will replace {x} in call to action (valid for topic=creator) */
  youtuber?: {
    ytid: string,
    name: string
  }
  /** topic to register to  */
  topic: keyof typeof NotificationTopics,
  className?: string,
}

/** notification button component */
export default function NotificationButton({ youtuber, topic, className }: NotificationButtonProps) {
  const { fcmToken, notificationPermissionStatus, isOnIOS_without_NotificationAPI, ManualSetPermission } = useNotificationToken();
  const [instructionDialogOpen, setInstructionDialogOpen] = useState(false);

  let callToAction = callToActionList[topic].replace("{x}", youtuber?.name || "");
  let btnColor: string;
  let btnText: string

  // dynamic content according to permission status
  switch (notificationPermissionStatus) {
    case "granted":
      btnColor = "gold";
      btnText = "הפעל התראות"
      break;
    case "denied":
      btnColor = "red";
      btnText = "ביטול חסימת התראות"
      break;
    case "default":
      btnColor = "green";
      btnText = "אשר התראות באתר"
      callToAction = " מנייטפול על סרטונים, איוונטים וחדשות?"
      break;
    default:
      btnColor = "gray";
      btnText = "טוען"
  }

  //get subscription state
  const { isLoading, isValidating, data: isSubscribed_res, mutate }: SWRResponse<ActionResponseType<typeof notificationsCheckSubscription_action>> = useSWR(fcmToken ? topic + youtuber?.ytid : null, {
    fetcher: () => notificationsCheckSubscription_action(fcmToken, topic, youtuber?.ytid),
    "fallbackData": false,
    "keepPreviousData": true,
    "refreshInterval": 0,
    "errorRetryCount": 0
  } as SWRConfiguration_Custom)

  /** shows loader & blocking sumption while getting essential data */
  const tokenLoading = !fcmToken && notificationPermissionStatus == "granted" ||//while obtaining token
    isLoading; //while checking subscription status

  /** subscription status - default to false */
  const isSubscribed = isSubscribed_res?.isSubscribed || false;


  const [afterActivationEffect, setAfterActivationEffect] = useState(false);
  const activationEffect = useCallback(handleClick, [ManualSetPermission, fcmToken, isSubscribed, mutate, notificationPermissionStatus, topic, youtuber?.ytid])
  useEffect(() => {
    if (afterActivationEffect && notificationPermissionStatus == "granted" && fcmToken) {
      setAfterActivationEffect(false);
      activationEffect();
    }
  }, [afterActivationEffect, fcmToken, activationEffect, notificationPermissionStatus])

  if (isOnIOS_without_NotificationAPI)
    return <IOSInstallPrompt className={ className } />

  //if failed to check if subscribed - don't render component
  if (isSubscribed_res && "error" in isSubscribed_res)
    return;


  //while awaiting permission/initial-sub-status - render inactive anchored widget
  if (!notificationPermissionStatus || isLoading && !isValidating)
    return <AnchoredWidget { ...{ callToAction, btnColor, btnText, handleClick, isSubscribed, disabled: true, className, topic } } />;


  // dynamic content according to subscription status
  if (isSubscribed) {
    btnColor = "gray";
    btnText = "ביטול התראות"
  }

  return (<>
    {
      !isSubscribed && topic != "test" &&
      <FlyWidget { ...{ callToAction, btnColor, btnText, handleClick, disabled: tokenLoading } } />
    }
    <AnchoredWidget { ...{ callToAction, btnColor, btnText, handleClick, isSubscribed, disabled: tokenLoading, className, topic } } />
    <CustomDialog open={ notificationPermissionStatus == "denied" && instructionDialogOpen } noBtns
      title={ <span className="hidden invisible">הוראות הפעלת התראות</span> }
      className="p-4 sm:p-8"
    >
      <div>
        <NotificationInstructions />
        <Button variant={ "outline" } className="mt-4" onClick={ () => setInstructionDialogOpen(false) }>סגור הוראות</Button>
      </div>
    </CustomDialog>
  </>
  )

  async function handleClick() {
    let token = fcmToken + "";

    if (notificationPermissionStatus == "denied")
      return setInstructionDialogOpen(true)

    if (notificationPermissionStatus == "default") {

      const toastID = toast.loading("יש לאשר שליחת התראות בדיאלוג דפדפן..")

      document.querySelector("main")?.style.setProperty("opacity", "0.15");
      const tout = setTimeout(() => document.querySelector("main")?.style.setProperty("opacity", "1"), 1000 * 10);
      const permission = await Notification.requestPermission(); //await for user action (browser UI)
      clearTimeout(tout);
      document.querySelector("main")?.style.setProperty("opacity", "1")

      toast.dismiss(toastID) //remove loader
      if (permission == "granted") {

        toast.info(<div>
          מבטיחים לא להספים :)
          <p>כעט ניתן לבחור בהתראות שתרצו לקבל.</p>
        </div>, { "duration": 10000 }) //GRANTED

        //on ios set manually case apple is shit
        isIOS && ManualSetPermission("granted")

        //trigger after-activation effect -auto subscribe
        setAfterActivationEffect(true)
      }

      if (permission == "denied") { //denied (info user & exit)
        toast.error(<>
          אוף, לא אישרת לנו - אפשר לשנות דרך כפתור ההגדרות
          <CgOptions className="bg-gray-200 aspect-square rounded-full p-1 text-black" fill="black" size={ "2em" } />
        </>)
      }
      return;
    }

    const toastID = toast.loading("מתחבר לשרת התראות..")
    const dynAction = isSubscribed ? notificationsUnregisterFromTopic_action : notificationsRegisterToTopic_action;
    const res = await dynAction(token, topic, youtuber?.ytid)
    toast.dismiss(toastID) //remove loader

    mutate(); //get new data about subscription state 

    if ("error" in res)
      return toast.warning(res.error)

    if (res.failureCount) {
      return toast.error(<>
        פעולה נכשלה, יש לנסות מאוחר יותר
        <FaSadTear className="inline" />
      </>);
    }

    isSubscribed && toast.error("אוף מעכשיו לא תקבל התראות..")
    !isSubscribed && toast.success("יאי! מעכשיו תקבל התראות :)")
  }
}

function FlyWidget({ callToAction, btnColor, btnText, handleClick, disabled }:
  { callToAction: string, btnColor: string, btnText: string, handleClick: () => Promise<any>, disabled: boolean }) {
  const ignoreNotificationPrompt = sessionStorage.ignoreNotificationPrompt == "true"
  const [visible, setVisible] = useState(!ignoreNotificationPrompt);
  const autoOpenAfter_x_Seconds = 10;

  const autoClose = useDebounce({
    delay: (autoOpenAfter_x_Seconds + 20) * 1000,
    callback: () => setVisible(false)
  })

  //on ini / each time a permission state changes - reset timer for auto-close
  useEffect(() => {
    autoClose();
  }, [callToAction, autoClose])

  if (ignoreNotificationPrompt)
    return;

  return <aside className={ cn("noAds group fixed z-30 -end-3 max-sm:bottom-24 sm:top-[70%]  p-2 pe-5",
    "flex flex-col gap-2 items-center justify-center max-w-72 ",
    "bg-slate-800 border-2 border-nfBlue-400 border-e-0 rounded-s-xl shadow-xl shadows-2 shadow-black/80 text-center",
    "hover:end-0 transition-all ease-in-out",
    "animate-in slide-in-from-left fill-mode-both duration-500",
    !visible && "animate-out slide-out-to-left",
  ) } style={ { animationDelay: visible && (autoOpenAfter_x_Seconds + "s") || "0s" } }
    onMouseOver={ () => autoClose()/** on hover reset close time as user might be interested */ }
  >
    <div className="flex flex-col gap-1 select-none">
      <IoMdCloseCircle
        onClick={ () => {
          setVisible(false);
          sessionStorage.ignoreNotificationPrompt = true;
        } }
        className="absolute top-1 end-3 group-hover:end-0 transition-[inset] duration-500 text-red-600 hover:text-red-400 text-2xl cursor-pointer z-10 pointer-events-auto rounded-full active:scale-95" />
      <h5 role="heading" className="text-nfPurple-300 leading-6 pe-4">
        <AnimatedIcon icon="messaging" loop play className="text-4xl me-1" />
        להפעיל התראות?

      </h5>
      <p className="leading-4 text-sm">
        רוצים לקבל התראה { callToAction }
      </p>
    </div>
    <form className="w-full" action={ async () => { await handleClick() } }>
      <ButtonNF color={ btnColor as "green" } className="py-2 w-full" disabled={ disabled } isLoading={ disabled }>
        <FaBell />
        { btnText }
      </ButtonNF>
    </form>
  </aside>
}

function AnchoredWidget({ callToAction, btnColor, btnText, handleClick, isSubscribed, disabled, className, topic }:
  { callToAction: string, btnColor: string, btnText: string, handleClick: () => Promise<any>, topic: string, isSubscribed: boolean | undefined, disabled: boolean, className?: string }) {

  function topicTitle() {
    switch (topic) {
      case "creator": return <span className="text-rose-300">יוצר</span>;
      case "videos": return <span className="text-orange-300">סרטונים</span>;
      case "generic": return "עדכונים";
      case "test": return <span className="text-red-600">בדיקה</span>;
    }
  }

  return <Section classList={ cn("group",
    "text-center sm:py-6",
    className
  ) } >
    <div className="noAds flex flex-col sm:flex-row gap-4 items-center justify-center ">
      <AnimatedIcon icon="messaging" loop play className="text-8xl me-1" />
      <div className="flex flex-col gap-1 pointer-events-none">
        <h4 role="heading" className="text-nfPurple-300 leading-6">
          { isSubscribed && "לכבות" || "להפעיל" } התראות { topicTitle() }?
        </h4>
        <p className="">
          רוצים  { isSubscribed && "להפסיק" } לקבל התראה { callToAction }
          { !isSubscribed && <span className="text-xs block italic opacity-80"> (תוכלו תמיד לחזור לפה ולכבות את ההתראות במידה ותרצו)</span> }
        </p>
        <form className="w-full pointer-events-auto" action={ async () => { await handleClick() } }>
          <ButtonNF color={ btnColor as "green" } className="py-2 w-full" disabled={ disabled } isLoading={ disabled }>
            <FaBell />
            { btnText }
          </ButtonNF>
        </form>
      </div>
    </div>
  </Section>
}

function IOSInstallPrompt({ className }: { className?: string }) {
  return (
    <Section classList={ cn("group",
      "text-center sm:py-6",
      className
    ) } >
      <div className="noAds flex flex-col sm:flex-row gap-4 items-center justify-center ">
        <AnimatedIcon icon="messaging" loop play className="text-8xl me-1" />
        <div className="flex flex-col gap-1 pointer-events-none">
          <h4 role="heading" className="text-nfPurple-300 leading-6">
            הפעלת התראות
          </h4>
          <p className="">
            <b>רוצים לקבל התראות למכשיר ה IOS שלכם?</b> <span className="text-lime-300">
              הוסיפו את האפליקציה למסך הבית!
            </span>
          </p>
          <div className=" bg-slate-800 p-2 rounded-lg border border-yellow-300 font-black">
            לוחצים על
            <IoShareOutline className="text-lime-300 animate-pulse inline mx-2" size="1.5em" />
            ואז על
            <IoAddCircleOutline className="text-lime-300 animate-pulse inline mx-2" size="1.5em" />
            הוסף למסך הבית.
          </div>
        </div>
      </div>
    </Section>
  )
}
